import React from 'react';
import Status from './routing/Status';

import loadable from '@loadable/component';

import App from './containers/App/App';
import Home from './containers/Home/Home';
import Login from './containers/Login/Login';
import PasswordRecovery from './containers/Login/PasswordRecovery';
import LoginSuccess from './containers/LoginSuccess/LoginSuccess';
import LogoutSuccess from './containers/LoginSuccess/LogoutSuccess';
import NotFound from './containers/NotFound/NotFound';

import Admin from './containers/Admin/Admin.js';
import AdminLogin from './containers/Admin/AdminLogin.js';
import EquipsnSync from './containers/EquipsnSync/EquipsnSync.js';

import AddClient from './containers/Client/AddClient.js';
import ClientList from './containers/Client/Clients.js';
import ValidateClientView from './containers/Client/ValidateClientView';

import FicheList from './containers/Fiches/Fiches.js';
import AddFiche from './containers/Fiches/AddFiche.js';
import EditFiche from './containers/Fiches/EditFiche.js';

import ProductList from './containers/Products/Products.js';
import AddProduct from './containers/Products/AddProduct.js';
import EditProduct from './containers/Products/EditProduct.js';

import GroupList from './containers/Groups/Groups.js';
import AddGroup from './containers/Groups/AddGroup.js';

import UserList from './containers/Users/Users.js';
import AddUser from './containers/Users/AddUser.js';

import ModuleList from './containers/Modules/Modules.js';
import AddModule from './containers/Modules/AddModule.js';

import SessionList from './containers/Sessions/Sessions';
import SessionExpired from './containers/Sessions/SessionExpired.js';

import ImportCSV from './containers/ImportCSV/ImportCSV.js';
import TicketList from './containers/Tickets/Tickets.js';
import HistoryList from './containers/History/History.js';

import ComClientList from './containers/Commercial/Client/Clients.js';
import AddClientPage from './containers/Commercial/Client/AddClientPage.js';
import AddCommercialPage from './containers/Commercial/Client/AddCommercialPage.js';
import CommercialList from './containers/Commercial/Client/CommercialList.js';

import LastEventsView from './containers/LastEvents/LastEventsView';
import Demo from './containers/Demo/Demo';

const Supervision = loadable(() => import('./containers/Supervision/TableSupervision'));
const SupervisionErrors = loadable(() => import('./containers/Supervision/SupervisionErrors'));
const TimelineEventsPage = loadable(() => import('./containers/TimelineEvents/TimelineEventsPage'));
const DashboardView = loadable(() => import('./containers/Dashboard/DashboardView'));

import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { isLoaded as isAuthLoaded, load as loadAuth } from 'redux/modules/auth';

function makeGuard(check, redirectTo) {
  class Guard extends React.Component {
    render() {
      const { user, authLoaded } = this.props;
      if (!authLoaded) {
        return null;
      }
      if (!check(user, this.props)) {
        return <Redirect to={redirectTo} />;
      }
      return this.props.children || null;
    }
  }

  const Connected = connect(state => ({ user: state.auth.user, authLoaded: state.auth && state.auth.loaded }))(Guard);
  Connected.redirectTo = redirectTo;
  Connected.check = check;
  return Connected;
}

const ClientGuard = makeGuard((user) => !!(user && user.role && (user.role.indexOf('client') > -1 || user.role === 'demo' || user.role.indexOf('user') > -1)), '/');
const AdminGuard = makeGuard((user) => !!(user && user.role === 'admin'), '/login');
const UserGuard = makeGuard((user) => !!(user && user.role && user.role.indexOf('user') > -1), '/');
const SuperUserGuard = makeGuard((user) => !!(user && user.role && user.role.indexOf('user') > -1 && user.super_user !== 0), '/');
const CommercialGuard = makeGuard((user) => !!(user && user.role === 'commercial'), '/');

function withGuard(Guard, Wrapped) {
  class Guarded extends React.Component {
    render() {
      const { children, ...rest } = this.props;
      return (
        <Guard>
          <Wrapped {...rest}>{children}</Wrapped>
        </Guard>
      );
    }
  }

  Guarded.loadData = function loadData(ctx) {
    const { store, staticContext } = ctx;
    const ensureAuth = !isAuthLoaded(store.getState()) ? store.dispatch(loadAuth()) : Promise.resolve();

    return Promise.resolve(ensureAuth).then(() => {
      const user = store.getState().auth.user;
      const ok = Guard && typeof Guard.check === 'function' ? Guard.check(user, ctx) : true;

      if (!ok) {
        if (staticContext && Guard && Guard.redirectTo) {
          staticContext.url = Guard.redirectTo;
        } else if (staticContext && Guard && Guard.redirectTo === undefined && Guard.redirectTo !== null) {
          staticContext.url = '/';
        }
        return null;
      }

      if (Wrapped && typeof Wrapped.loadData === 'function') {
        return Wrapped.loadData(ctx);
      }

      if (Wrapped && typeof Wrapped.load === 'function') {
        return Wrapped.load().then((mod) => {
          const Loaded = mod && (mod.default || mod);
          if (Loaded && typeof Loaded.loadData === 'function') {
            return Loaded.loadData(ctx);
          }
          return null;
        });
      }

      return null;
    });
  };

  hoistNonReactStatics(Guarded, Wrapped);
  return Guarded;
}

export default function createRoutes() {
  const ClientOnly = (C) => withGuard(ClientGuard, C);
  const AdminOnly = (C) => withGuard(AdminGuard, C);
  const UserOnly = (C) => withGuard(UserGuard, C);
  const SuperUserOnly = (C) => withGuard(SuperUserGuard, C);
  const CommercialOnly = (C) => withGuard(CommercialGuard, C);

  return [
    {
      path: '/',
      component: App,
      routes: [
        { exact: true, path: '/', component: Home },
        { path: '/home', component: Home },

        { path: '/admin/login', component: AdminLogin },
        { path: '/login', component: Login },
        { exact: true, path: '/forgot-password', component: PasswordRecovery },
        { exact: true, path: '/reset-password', component: PasswordRecovery },
        { path: '/demo', component: Demo },
        { path: '/sessionExpired', component: SessionExpired },
        { path: '/logout', component: LogoutSuccess },
        { path: '/validateClient/:token', component: ValidateClientView },
        { path: '/loginSuccess', component: LoginSuccess },

        { exact: true, path: '/client/groups', component: ClientOnly(GroupList) },
        { exact: true, path: '/client/addGroup', component: ClientOnly(AddGroup) },
        { exact: true, path: '/client/users', component: ClientOnly(UserList) },
        { exact: true, path: '/client/adduser', component: ClientOnly(AddUser) },
        { path: '/client/supervision/:userId', component: ClientOnly(Supervision) },
        { exact: true, path: '/client/supervision', component: ClientOnly(Supervision) },
        { exact: true, path: '/client/supervisionErrors', component: ClientOnly(SupervisionErrors) },
        { path: '/client/supervisionErrors/:userId', component: ClientOnly(SupervisionErrors) },
        { exact: true, path: '/client/products', component: ClientOnly(ProductList) },
        { path: '/client/products/:ptype', component: ClientOnly(ProductList) },
        { exact: true, path: '/client/addProduct', component: ClientOnly(AddProduct) },
        { path: '/client/product/:productID/edit', component: ClientOnly(EditProduct) },
        { exact: true, path: '/client/tickets', component: ClientOnly(TicketList) },
        { exact: true, path: '/client/history', component: ClientOnly(HistoryList) },
        { exact: true, path: '/client/timelineEvents', component: ClientOnly(TimelineEventsPage) },
        { exact: true, path: '/client/LastEventsView', component: ClientOnly(LastEventsView) },

        { exact: true, path: '/commercial/clients', component: CommercialOnly(ComClientList) },
        { exact: true, path: '/commercial/addClientCom', component: CommercialOnly(AddClientPage) },

        { exact: true, path: '/admin', component: AdminOnly(Admin) },
        { exact: true, path: '/admin/equipsnSync', component: AdminOnly(EquipsnSync) },
        { path: '/admin/sessions', component: AdminOnly(SessionList) },
        { path: '/admin/LastEventsView/:id', component: AdminOnly(LastEventsView) },
        { path: '/:role/addClient', component: AdminOnly(AddClient) },
        { path: '/:role/AddCommercialPage', component: AdminOnly(AddCommercialPage) },
        { path: '/:role/commercialList', component: AdminOnly(CommercialList) },
        { path: '/:role/clientList', component: AdminOnly(ClientList) },
        { path: '/:role/products/:id/create', component: AdminOnly(AddProduct) },
        { path: '/:role/users/:id', component: AdminOnly(UserList) },
        { path: '/:role/supervision/:cid', component: AdminOnly(Supervision) },
        { path: '/:role/supervision/:cid/:uid', component: AdminOnly(Supervision) },
        { path: '/:role/supervisionErrors/:cid', component: AdminOnly(SupervisionErrors) },
        { path: '/:role/supervisionErrors/:cid/:uid', component: AdminOnly(SupervisionErrors) },
        { path: '/:role/modules', component: AdminOnly(ModuleList) },
        { path: '/:role/addModule', component: AdminOnly(AddModule) },
        { path: '/:role/groups/:id', component: AdminOnly(GroupList) },
        { path: '/:role/groups/:cid/create', component: AdminOnly(AddGroup) },
        { path: '/:role/users/:id/create', component: AdminOnly(AddUser) },
        { path: '/:role/fiches/:id', component: AdminOnly(FicheList) },
        { path: '/:role/fiches/:id/create', component: AdminOnly(AddFiche) },
        { path: '/:role/:cid/fiche/:ficheID/edit', component: AdminOnly(EditFiche) },
        { path: '/:role/tickets/:cid', component: AdminOnly(TicketList) },
        { path: '/:role/history/:cid', component: AdminOnly(HistoryList) },
        { path: '/:role/products/:id', component: AdminOnly(ProductList) },
        { path: '/:role/products/:id/:ptype', component: AdminOnly(ProductList) },
        { path: '/:role/:cid/product/:productID/edit', component: AdminOnly(EditProduct) },
        { path: '/:role/importCSV', component: AdminOnly(ImportCSV) },
        { path: '/:role/:cid/timelineEvents', component: AdminOnly(TimelineEventsPage) },
        { path: '/:role/:cid/timelineEvents/:pid', component: AdminOnly(TimelineEventsPage) },
        { path: '/:role/dashboardView/:id', component: AdminOnly(DashboardView) },
        { path: '/:role/timelineEvents/:id', component: AdminOnly(TimelineEventsPage) },

        { path: '/superuser/adduser', component: SuperUserOnly(AddUser) },
        { path: '/superuser/supervision2/:uid', component: SuperUserOnly(Supervision) },
        { path: '/superuser/supervisionErrors2/:uid', component: SuperUserOnly(SupervisionErrors) },
        { path: '/superuser/LastEventsView', component: SuperUserOnly(LastEventsView) },
        { path: '/:role/users', component: SuperUserOnly(UserList) },
        { path: '/:role/groups', component: SuperUserOnly(GroupList) },
        { path: '/:role/groups/:id', component: SuperUserOnly(GroupList) },
        { path: '/:role/groups/:cid/create', component: SuperUserOnly(AddGroup) },
        { path: '/:role/group/addGroup', component: SuperUserOnly(AddGroup) },
        { path: '/:role/addProduct', component: SuperUserOnly(AddProduct) },

        { path: '/:role/supervision', component: UserOnly(Supervision) },
        { path: '/:role/supervisionErrors', component: UserOnly(SupervisionErrors) },
        { path: '/:role/fiches', component: UserOnly(ProductList) },
        { path: '/:role/fiches/:ptype', component: UserOnly(ProductList) },
        { path: '/:role/tickets', component: UserOnly(TicketList) },
        { path: '/:role/product/:productID/edit', component: UserOnly(EditProduct) },
        { path: '/:role/timelineEvents', component: UserOnly(TimelineEventsPage) },
        { path: '/:role/timelineEvents/:pid', component: UserOnly(TimelineEventsPage) },
        { path: '/user/LastEventsView', component: UserOnly(LastEventsView) },

        {
          component: (props) => (
            <Status code={404} {...props}>
              <NotFound {...props} />
            </Status>
          )
        }
      ]
    }
  ];
}
